import type { MetricDefinition } from './metrics.js';
import type { Operator } from './operators.js';

export const SEVERITIES = ['warning', 'critical'] as const;

export type Severity = (typeof SEVERITIES)[number];

export type AlertKind = 'fired' | 'reminder';

/**
 * Overall client state shown on the dashboard.
 * - unreachable: last poll could not reach node_exporter or the app endpoint
 * - firing:      at least one rule is firing
 * - stale:       no poll recorded within the last three intervals
 * - no_data:     never polled
 */
export type ClientHealth = 'healthy' | 'firing' | 'unreachable' | 'stale' | 'no_data';

export interface RuleDto {
  id: string;
  metric: string;
  operator: Operator;
  threshold: number;
  consecutive: number;
  severity: Severity;
  remindEveryMinutes: number;
}

export interface ClientDto {
  id: string;
  name: string;
  /** Host (usually a local IP) the client is reached at, if known. */
  ip?: string;
  metrics: MetricDefinition[];
  rules: RuleDto[];
}

export interface RuleStateDto {
  ruleId: string;
  consecutiveBreaches: number;
  firing: boolean;
}

export interface LatestSnapshotDto {
  timestamp: string;
  metrics: Record<string, number>;
}

export interface ClientStatusDto {
  client: ClientDto;
  health: ClientHealth;
  latest: LatestSnapshotDto | null;
  ruleStates: RuleStateDto[];
}

export interface OverviewDto {
  pollIntervalMinutes: number;
  clients: ClientStatusDto[];
}

export interface AggregateDto {
  avg: number;
  min: number;
  max: number;
}

export interface SeriesPointDto {
  /** Bucket start, ISO 8601. */
  t: string;
  polls: number;
  values: Record<string, AggregateDto>;
}

export interface SeriesDto {
  clientId: string;
  from: string;
  to: string;
  binMs: number;
  points: SeriesPointDto[];
}

export interface AlertDto {
  id: string;
  clientId: string;
  ruleId: string;
  metric: string;
  operator: Operator;
  threshold: number;
  value: number | null;
  severity: Severity;
  kind: AlertKind;
  consecutiveBreaches: number;
  sentAt: string;
  delivered: boolean;
  error?: string;
}

export interface LoginRequestDto {
  /** Username or email. */
  identifier: string;
  password: string;
}

export interface LoginResponseDto {
  token: string;
  username: string;
  expiresAt: string;
}

export interface ApiErrorDto {
  error: string;
}
